// SetupTour — the single onboarding spine.
//
// One component drives all three parts off GET /api/onboarding/status +
// server-side progress (user_state):
//   Part 1  SetupSteps      — locked-Next setup checklist
//   Part 2  FeatureReveals  — 10-stop spotlight walkthrough
//   Part 3  AiFinale        — ask-your-AI finale → celebration → retire
// plus the post-tour EchoHint. It self-hides when there's nothing to show.
//
// Safety: this whole tree is mounted inside a TabBoundary in the shell, so
// a bug here can never crash the dashboard. Progress is server-side only
// (no localStorage) so it resumes cross-device. The component must never
// throw during render — every async path is guarded.

import React from 'react';
import { readPersistedJwtDirect } from '../../lib/authedFetch';
import { loadTourState, saveTourState } from './tourState';
import {
  PHASES, deriveView, mergeTourDefaults, orderSetupSteps,
  resumeSetupIndex, allMustHaveDone, markCompleted, shouldShowCelebration,
  markCelebrated, shouldShowEcho, echoAfterShown, echoAfterAiUse, REVEAL_COUNT,
  clampReveal,
} from './tourLogic';
import SetupSteps from './SetupSteps';
import FeatureReveals from './FeatureReveals';
import AiFinale from './AiFinale';
import EchoHint, { Celebration } from './EchoHint';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body && body.error) || `HTTP ${res.status}`);
  return body;
}

export default function SetupTour({ currentUser, onGotoTab = () => {}, onGotoIntent = () => {}, activeTab }) {
  const uid = currentUser && currentUser.id;

  const [tourState, setTourStateRaw] = React.useState(() => mergeTourDefaults(null));
  const [loaded, setLoaded] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [statusFailed, setStatusFailed] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [reopened, setReopened] = React.useState(false);
  const [awaitingAi, setAwaitingAi] = React.useState(false);
  const [aiErrored, setAiErrored] = React.useState(false);

  // Latest tourState in a ref so window-event handlers (mounted once) always
  // persist against fresh state.
  const stateRef = React.useRef(tourState);
  stateRef.current = tourState;

  // Persist a mutation: compute next from the latest state, write it
  // server-side, and mirror into React state.
  const update = React.useCallback((mutator) => {
    const prev = stateRef.current;
    const next = mergeTourDefaults(typeof mutator === 'function' ? mutator(prev) : mutator);
    stateRef.current = next;
    setTourStateRaw(next);
    saveTourState(uid, next);
    return next;
  }, [uid]);

  // ── Load server-side progress ───────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;
    if (!uid) { setLoaded(false); return; }
    (async () => {
      const s = await loadTourState(uid);
      if (cancelled) return;
      stateRef.current = s;
      setTourStateRaw(s);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // ── Fetch onboarding status (single source of truth) ────────────────
  const fetchStatus = React.useCallback(async () => {
    if (!uid) return;
    setRefreshing(true);
    try {
      const body = await apiFetch('/api/onboarding/status');
      setStatus(body);
      setStatusFailed(false);
    } catch {
      // A failed status must HIDE the setup surface quietly — never crash.
      setStatus(null);
      setStatusFailed(true);
    } finally {
      setRefreshing(false);
    }
  }, [uid]);

  React.useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Refetch when the tab regains focus and after in-app tab changes.
  React.useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') fetchStatus(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchStatus]);

  React.useEffect(() => { if (activeTab) fetchStatus(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reopen from the sidebar "Setup" entry ───────────────────────────
  React.useEffect(() => {
    const onOpen = () => setReopened(true);
    window.addEventListener('setup-tour:open', onOpen);
    return () => window.removeEventListener('setup-tour:open', onOpen);
  }, []);

  // ── Finale: watch for the AI's first response / failure ─────────────
  const awaitingRef = React.useRef(false);
  awaitingRef.current = awaitingAi;
  React.useEffect(() => {
    const onResponded = () => {
      if (!awaitingRef.current) return;
      setAwaitingAi(false);
      setAiErrored(false);
      // Complete the tour + arm the one-time celebration.
      update(prev => markCompleted(prev, new Date().toISOString()));
    };
    const onError = () => { if (awaitingRef.current) { setAwaitingAi(false); setAiErrored(true); } };
    window.addEventListener('setup-tour:ai-responded', onResponded);
    window.addEventListener('setup-tour:ai-error', onError);
    return () => {
      window.removeEventListener('setup-tour:ai-responded', onResponded);
      window.removeEventListener('setup-tour:ai-error', onError);
    };
  }, [update]);

  // ── Post-tour: count AI uses toward the echo's 5-use cap ────────────
  React.useEffect(() => {
    const onUsed = () => {
      if (stateRef.current.phase !== PHASES.DONE) return;
      update(prev => ({ ...prev, echo: echoAfterAiUse(prev.echo) }));
    };
    window.addEventListener('setup-tour:ai-used', onUsed);
    return () => window.removeEventListener('setup-tour:ai-used', onUsed);
  }, [update]);

  // ── Post-tour: throttle the echo hint to once per day (server-side) ──
  React.useEffect(() => {
    const ts = stateRef.current;
    if (ts.phase !== PHASES.DONE || reopened) return;
    const today = new Date().toISOString().slice(0, 10);
    if (!shouldShowEcho(ts.echo, Date.now(), today)) return;
    if (ts.echo.lastShownDate === today) return;
    update(prev => ({ ...prev, echo: echoAfterShown(prev.echo, today) }));
  }, [tourState.phase, tourState.echo, reopened, update]);

  // ── Derived view ────────────────────────────────────────────────────
  const orderedSteps = React.useMemo(() => orderSetupSteps(status && status.steps), [status]);
  const mustDone = allMustHaveDone(status && status.steps);

  if (!uid || !loaded) return null;

  const view = deriveView({ tourState, status, statusFailed, reopened });

  // Product guard: don't auto-nag an already-onboarded operator who never
  // engaged the tour. They can still open it from the sidebar (reopened).
  const pristine = tourState.setupStepIndex === 0 && !tourState.completedAt && tourState.revealIndex === 0;
  const suppressSetupNag = view === 'setup' && tourState.phase === PHASES.SETUP && mustDone && pristine && !reopened;

  // Navigation for a reveal stop: deep-link via intent (workspace-aware) or
  // concrete tab id.
  const navigateStop = (stop) => {
    try {
      if (stop.intent) onGotoIntent(stop.intent);
      else if (stop.tab) onGotoTab(stop.tab);
    } catch { /* navigation is best-effort */ }
  };

  const retire = () => update(prev => ({ ...markCompleted(prev, new Date().toISOString()), celebrated: true }));

  // CTA navigation for a setup step — deep-link through the app's tab nav
  // rather than a raw href. The status endpoint's cta_href values look like
  // '/settings#billing'; map the leading path segment to a tab id.
  const handleCtaNav = (step) => {
    // The "Import your first book" step opens the WowOnboarding upload flow.
    if (step && step.key === 'first_book_source') {
      try { window.dispatchEvent(new CustomEvent('setup-tour:open-upload')); } catch { /* best-effort */ }
      return;
    }
    const href = String((step && step.cta_href) || '');
    const seg = href.replace(/^\//, '').split(/[#?/]/)[0];
    const map = { settings: 'settings', import: 'home', clients: 'clients' };
    const tab = map[seg] || seg;
    if (tab) { try { onGotoTab(tab); } catch { /* best-effort */ } }
  };

  // ── Render ──────────────────────────────────────────────────────────
  const celebrating = shouldShowCelebration(tourState);

  let surface = null;

  if (view === 'setup' && !suppressSetupNag) {
    surface = (
      <SetupSteps
        status={status}
        tourState={tourState}
        currentIndex={resumeSetupIndex(tourState, orderedSteps)}
        refreshing={refreshing}
        onRefresh={fetchStatus}
        onCta={handleCtaNav}
        onNext={() => update(prev => ({ ...prev, setupStepIndex: Math.min((prev.setupStepIndex || 0) + 1, Math.max(orderedSteps.length - 1, 0)) }))}
        onSkipStep={(step) => update(prev => ({ ...prev, skipped: { ...prev.skipped, [step.key]: true } }))}
        onFinishLater={() => { if (reopened) setReopened(false); else update(prev => ({ ...prev, minimized: true })); }}
        onStartReveals={() => { setReopened(false); update(prev => ({ ...prev, phase: PHASES.REVEALS, revealIndex: 0, minimized: false })); }}
      />
    );
    // A minimized Part-1 card collapses to a Resume pill.
    if (tourState.minimized && !reopened) {
      surface = (
        <button
          onClick={() => update(prev => ({ ...prev, minimized: false }))}
          className="fixed z-40 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg"
          style={{ bottom: 20, left: 250 }}
        >
          Resume setup →
        </button>
      );
    }
  } else if (view === 'reveals') {
    surface = (
      <FeatureReveals
        index={clampReveal(tourState.revealIndex, REVEAL_COUNT)}
        onNavigate={navigateStop}
        onIndexChange={(i) => update(prev => ({ ...prev, revealIndex: clampReveal(i, REVEAL_COUNT) }))}
        onFinish={() => update(prev => ({ ...prev, phase: PHASES.FINALE }))}
        onSkip={retire}
      />
    );
  } else if (view === 'finale') {
    surface = (
      <AiFinale
        awaiting={awaitingAi}
        errored={aiErrored}
        onSubmit={(text) => {
          setAiErrored(false);
          setAwaitingAi(true);
          try { window.dispatchEvent(new CustomEvent('setup-tour:ask-ai', { detail: { text } })); } catch {}
        }}
        onSkip={retire}
      />
    );
  }

  // Post-tour echo hint (independent of the celebration).
  const today = new Date().toISOString().slice(0, 10);
  const echoVisible = view === 'echo' && shouldShowEcho(tourState.echo, Date.now(), today);
  const echo = echoVisible ? (
    <EchoHint
      onClick={() => { try { window.dispatchEvent(new CustomEvent('setup-tour:ask-ai', { detail: { text: '' } })); } catch {} }}
      onDismiss={() => update(prev => ({ ...prev, echo: { ...prev.echo, lastShownDate: today } }))}
    />
  ) : null;

  return (
    <>
      {surface}
      {echo}
      {celebrating && <Celebration onClose={() => update(prev => markCelebrated(prev))} />}
    </>
  );
}
